import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { FileText, Save, Eye, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DOC_TYPES = [
  { key: "quote",      label: "Quotes" },
  { key: "invoice",    label: "Invoices" },
  { key: "receipt",    label: "Receipts" },
  { key: "work_order", label: "Work Orders" },
];

/** Phase F.5 Batch 17 — PDF Template System admin page. */
export default function PdfTemplates() {
  const [catalog, setCatalog] = useState({});
  const [docType, setDocType] = useState("quote");
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewKey, setPreviewKey] = useState(null);

  const loadCatalog = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/pdf-templates/catalog`);
      setCatalog(r.data?.catalog || {});
    } catch (e) { /* */ }
  }, []);
  const loadCfg = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/pdf-templates/config`, { params: { doc_type: docType } });
      setCfg(r.data);
    } catch (e) { /* */ }
  }, [docType]);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);
  useEffect(() => { loadCfg(); }, [loadCfg]);

  const update = (patch) => setCfg((p) => ({ ...p, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        template_key: cfg.template_key, brand_color: cfg.brand_color,
        accent_color: cfg.accent_color, font_family: cfg.font_family,
        logo_url: cfg.logo_url, header_tagline: cfg.header_tagline,
        footer_text: cfg.footer_text,
        show_logo: cfg.show_logo, show_address: cfg.show_address, show_tax_id: cfg.show_tax_id,
      };
      const r = await axios.put(`${API}/pdf-templates/config`, body, { params: { doc_type: docType } });
      setCfg(r.data);
      toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const preview = async (key) => {
    setPreviewKey(key);
    try {
      const params = { doc_type: docType };
      if (key) params.template_key = key;
      const r = await axios.get(`${API}/pdf-templates/preview`, { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
    } catch (e) { toast.error("Preview failed"); }
    finally { setPreviewKey(null); }
  };

  return (
    <div className="space-y-4 p-4" data-testid="pdf-templates-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><FileText className="h-6 w-6" /> PDF Templates</h1>
        <p className="text-sm text-muted-foreground">
          Pick a layout per document type and customise branding. Preview before saving.
        </p>
      </div>

      <Tabs value={docType} onValueChange={setDocType}>
        <TabsList data-testid="pt-doc-tabs">
          {DOC_TYPES.map((d) => (
            <TabsTrigger key={d.key} value={d.key} data-testid={`pt-tab-${d.key}`}>{d.label}</TabsTrigger>
          ))}
        </TabsList>

        {DOC_TYPES.map((d) => (
          <TabsContent key={d.key} value={d.key}>
            <Card>
              <CardHeader>
                <CardTitle>{d.label} layout</CardTitle>
                <CardDescription>Pick the template, then customise colours and contact lines.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Template picker */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2" data-testid="pt-template-grid">
                  {(catalog[d.key] || []).map((tpl) => (
                    <div
                      key={tpl.key}
                      className={`p-3 border rounded-lg cursor-pointer transition ${cfg?.template_key === tpl.key ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/20" : "hover:bg-muted"}`}
                      onClick={() => update({ template_key: tpl.key })}
                      data-testid={`pt-template-${tpl.key}`}
                    >
                      <div className="font-medium flex items-center gap-2">
                        {tpl.label}
                        {cfg?.template_key === tpl.key && <Badge className="bg-emerald-600">selected</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{tpl.description}</p>
                      <Button size="sm" variant="outline" className="mt-2" onClick={(e) => { e.stopPropagation(); preview(tpl.key); }} disabled={previewKey === tpl.key} data-testid={`pt-preview-${tpl.key}`}>
                        {previewKey === tpl.key ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Eye className="h-3 w-3 mr-1" />} Preview
                      </Button>
                    </div>
                  ))}
                </div>

                {/* Branding */}
                {cfg && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t">
                    <div><Label>Brand colour</Label><Input value={cfg.brand_color} onChange={(e) => update({ brand_color: e.target.value })} data-testid="pt-brand-color" /></div>
                    <div><Label>Accent colour</Label><Input value={cfg.accent_color} onChange={(e) => update({ accent_color: e.target.value })} data-testid="pt-accent-color" /></div>
                    <div><Label>Font family</Label><Input value={cfg.font_family} onChange={(e) => update({ font_family: e.target.value })} data-testid="pt-font-family" /></div>
                    <div><Label>Logo URL</Label><Input value={cfg.logo_url || ""} onChange={(e) => update({ logo_url: e.target.value })} data-testid="pt-logo-url" /></div>
                    <div className="md:col-span-2"><Label>Header tagline</Label>
                      <Input value={cfg.header_tagline || ""} onChange={(e) => update({ header_tagline: e.target.value })} data-testid="pt-header-tagline" />
                    </div>
                    <div className="md:col-span-2"><Label>Footer text</Label>
                      <Textarea rows={2} value={cfg.footer_text || ""} onChange={(e) => update({ footer_text: e.target.value })} data-testid="pt-footer-text" />
                    </div>
                  </div>
                )}

                {cfg && (
                  <div className="flex flex-wrap gap-4">
                    <label className="text-sm flex items-center gap-2"><Switch checked={!!cfg.show_logo} onCheckedChange={(v) => update({ show_logo: v })} data-testid="pt-show-logo" /> Show logo</label>
                    <label className="text-sm flex items-center gap-2"><Switch checked={!!cfg.show_address} onCheckedChange={(v) => update({ show_address: v })} data-testid="pt-show-address" /> Show address</label>
                    <label className="text-sm flex items-center gap-2"><Switch checked={!!cfg.show_tax_id} onCheckedChange={(v) => update({ show_tax_id: v })} data-testid="pt-show-tax-id" /> Show tax ID</label>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button onClick={save} disabled={saving || !cfg} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pt-save-btn">
                    <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save"}
                  </Button>
                  <Button variant="outline" onClick={() => preview(null)} disabled={!cfg} data-testid="pt-preview-current-btn">
                    <Eye className="h-4 w-4 mr-1" /> Preview current
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
